export const TIME_ZONE_STORAGE_KEY = "ottagive-admin-time-zone";

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function isValidTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export function timeZoneOptions(localTimeZone = browserTimeZone()): string[] {
  const supportedValuesOf = (Intl as typeof Intl & {
    supportedValuesOf?: (key: "timeZone") => string[];
  }).supportedValuesOf;
  const supported = supportedValuesOf ? supportedValuesOf("timeZone") : [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "UTC"
  ];
  return [localTimeZone, "UTC", ...supported]
    .filter((value, index, values) => isValidTimeZone(value) && values.indexOf(value) === index);
}

export function timeZoneLabel(value: string, localTimeZone = browserTimeZone()): string {
  const name = value === "UTC" ? "UTC" : value.replaceAll("_", " ").replaceAll("/", " / ");
  return value === localTimeZone ? `Local — ${name}` : name;
}

/**
 * Plain-language distance to a future instant. Guarded actions state their own
 * expiry in absolute time; this reads next to it so nobody has to do the
 * subtraction while an incident is running.
 */
export function formatCountdown(value: string | null, now: Date = new Date()): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const minutes = Math.round((parsed.getTime() - now.getTime()) / 60000);
  if (minutes <= 0) return "any moment now";
  if (minutes < 60) return `in about ${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (remainder === 0) return `in about ${hours} hour${hours === 1 ? "" : "s"}`;
  return `in about ${hours}h ${remainder}m`;
}

export function formatTimestamp(
  value: string | null,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" },
  emptyValue = "—"
): string {
  if (!value) return emptyValue;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return emptyValue;
  const { timeZoneName = "short", ...dateOptions } = options;
  const formatted = new Intl.DateTimeFormat("en-US", { ...dateOptions, timeZone }).format(parsed);
  const zoneName = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName })
    .formatToParts(parsed)
    .find((part) => part.type === "timeZoneName")?.value;
  return zoneName ? `${formatted} ${zoneName}` : formatted;
}
